/** SDK-reported usage, never inferred from characters or used as a billing receipt. */
export interface ModelTokenUsage {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  total: number;
}

export interface PromptTelemetry {
  version: "sdk-usage-v1";
  sdkRetryEvents: number;
  assistantMessages: number;
  usageReports: number;
  usage: ModelTokenUsage | null;
}

export interface ModelCallReceipt {
  version: "model-call-v1";
  id: string;
  sessionId: string;
  agentId: string;
  role: string;
  model?: string;
  inputHash: string;
  status: "running" | "succeeded" | "failed";
  startedAt: string;
  finishedAt?: string;
  elapsedMs?: number;
  telemetry?: PromptTelemetry;
}

export function emptyUsage(): ModelTokenUsage {
  return { input: 0, output: 0, cacheRead: 0, cacheWrite: 0, total: 0 };
}

const count = (value: unknown): value is number => Number.isSafeInteger(value) && (value as number) >= 0;
export function validUsage(value: unknown): value is ModelTokenUsage {
  if (!value || typeof value !== "object") return false;
  const usage = value as ModelTokenUsage;
  return [usage.input, usage.output, usage.cacheRead, usage.cacheWrite, usage.total].every(count) && usage.total > 0;
}

export function summarizeModelCalls(records?: Record<string, ModelCallReceipt>) {
  const usage = emptyUsage();
  let invalidRecords = 0;
  const calls = Object.entries(records ?? {}).flatMap(([key, record]) => {
    if (!record || record.version !== "model-call-v1" || record.id !== key
      || typeof record.agentId !== "string" || typeof record.startedAt !== "string"
      || !Number.isFinite(Date.parse(record.startedAt))
      || !["running", "succeeded", "failed"].includes(record.status)) {
      invalidRecords++;
      return [];
    }
    return [record];
  }).sort((a, b) => a.startedAt.localeCompare(b.startedAt) || a.id.localeCompare(b.id));
  let sdkRetryEvents = 0, assistantMessages = 0, usageReports = 0, measuredCalls = 0;
  for (const call of calls) {
    const metric = call.telemetry;
    if (!metric || metric.version !== "sdk-usage-v1"
      || ![metric.sdkRetryEvents, metric.assistantMessages, metric.usageReports].every(count)
      || metric.usageReports > metric.assistantMessages) continue;
    measuredCalls++;
    sdkRetryEvents += metric.sdkRetryEvents;
    assistantMessages += metric.assistantMessages;
    if (metric.usageReports && validUsage(metric.usage)) {
      usageReports += metric.usageReports;
      for (const key of Object.keys(usage) as (keyof ModelTokenUsage)[]) usage[key] += metric.usage[key];
    }
  }
  return {
    calls, invalidRecords, requestCount: calls.length,
    succeeded: calls.filter(c => c.status === "succeeded").length,
    failed: calls.filter(c => c.status === "failed").length,
    unfinished: calls.filter(c => c.status === "running").length,
    measuredCalls, sdkRetryEvents, assistantMessages, usageReports,
    usage: usageReports ? usage : null
  };
}
